import { useTranslation } from 'react-i18next';
import { Badge, makeStyles, tokens } from '@fluentui/react-components';
import { useExtractionAccuracy, useMyAccess } from '../../services/conversionsApi';
import { badgeWords } from '../../lib/accuracyBadge';

const useStyles = makeStyles({
  chips: { display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'center', flexShrink: 0 },
});

/**
 * Beside the header's client (accuracy spec §3; ptc-docs plan 2026-11-02 §2): ADMINS ONLY, one chip per graded source
 * kind, in counts ("DR-15 · 7 exact in a row", "DR-15 · 4 of 6"). The report is not even asked for unless core says the
 * person is an admin, and the server sends the badge to an admin alone; a client with no graded pair shows nothing.
 */
export default function HeaderAccuracyBadge({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const access = useMyAccess();
  const isAdmin = !!access.data?.isAdmin;
  const report = useExtractionAccuracy(clientId, isAdmin && !!clientId);
  if (!isAdmin || !clientId || !report.data?.viewerIsAdmin) return null;
  const rows = report.data.byClient.filter((r) => r.clientId === clientId && r.dependable != null);
  if (rows.length === 0) return null;
  return (
    <div className={styles.chips} aria-label={t('accuracyPage.badge.headerLabel')} role="group">
      {rows.map((row) => {
        const words = badgeWords(row, report.data.dependableAfter);
        const source = t(`accuracyPage.byClient.source.${row.sourceKind}`, { defaultValue: row.sourceKind });
        return (
          <Badge key={row.sourceKind} appearance={row.dependable ? 'filled' : 'tint'} color={row.dependable ? 'success' : 'informative'}>
            {`${source} · ${t(words.key, words.params)}`}
          </Badge>
        );
      })}
    </div>
  );
}
